import { get, isEmpty, merge } from "lodash";
import { PlotData, Layout, PlotType } from "plotly.js";
import { doHidden, COLORWAY } from "../utils";
import { I2dFigure, I2dStructure, IBarChart, IDisplay, IResult } from "../types";
import { IOutputProps } from "./types";
import "./style.scss";
import { useMemo } from "react";
import ArchiveIcon from "./ArchiveIcon";
import { downloadData } from "./download";
import { ChartLoadingSkeleton } from "./loading";
import { isTemplateRefreshing } from "../dependencyIndex";
import RefreshingState from "../RefreshingState";
import AutoSizedPlot from "./AutoSizedPlot";

export interface IBarChartProps extends IOutputProps {
  template: IBarChart;
}

const renderLabel = (display: IDisplay) => {
  const label = display.label ? display.label : "";
  const info = display.info ? ` <span class="pb3-text-muted">${display.info}</span>` : "";
  return `${label}${info}`;
};

const transformData = (template: IBarChart, result: IResult): Array<Partial<PlotData>> => {
  return template.figures
    .map(
      (f) =>
        ({
          type: "bar" as PlotType,
          ...(template.horizontal && { orientation: "h" }),
          ...((f as I2dStructure).type === "structure"
            ? {
                name: renderLabel(f as I2dStructure),
                x: (get(result, (f as I2dStructure).name, []) as Array<any>).map((v) => v[(f as I2dStructure).x_value]),
                y: (get(result, (f as I2dStructure).name, []) as Array<any>).map((v) => v[(f as I2dStructure).y_value]),
              }
            : {
                name: renderLabel((f as I2dFigure).y_data),
                x: get(result, (f as I2dFigure).x_data.name, []) as Array<number | string>,
                y: get(result, (f as I2dFigure).y_data.name, []) as Array<number>,
              }),
        } as Partial<PlotData>)
    )
    .filter((v) => !isEmpty(v.x) && !isEmpty(v.y));
};

const createLayout = (template: IBarChart, result: IResult): Partial<Layout> => {
  const minimal = template.variant === "minimal";
  const [xaxis, yaxis] = !minimal
    ? [
        template.axis?.[0] || (template.figures?.[0] as I2dFigure).x_data,
        template.axis?.[1] || (template.figures?.[0] as I2dFigure).y_data,
      ]
    : [];
  const [xrange, yrange] = template.range || [];
  return merge(
    {
      title: template.label,
      colorway: COLORWAY,
      barmode: template.stack ? ("stack" as const) : ("group" as const),
    },
    xaxis && { xaxis: { title: renderLabel(xaxis) } },
    yaxis && { yaxis: { title: renderLabel(yaxis) } },
    xrange && { xaxis: { range: get(result, xrange) } },
    yrange && { yaxis: { range: get(result, yrange) } },
    template.horizontal && { margin: { pad: 10 }, yaxis: { automargin: true } }
  );
};

const BarChart = (props: IBarChartProps) => {
  const { template, result, metadata } = props;
  const data = useMemo(() => transformData(template, result), [template, result]);
  const layout = useMemo(() => createLayout(template, result), [template, result]);
  const containerClassName = template.height ? "chart-container" : "small-chart-container";
  const refreshing = isTemplateRefreshing(template, metadata, props.mutators.getPath);

  if (doHidden(props)) {
    return null;
  }

  if (isEmpty(data) && refreshing) {
    return (
      <ChartLoadingSkeleton
        className={containerClassName}
        height={template.height}
        label={template.label || template.name}
        variant="bar"
      />
    );
  }

  if (isEmpty(data)) {
    return null;
  }

  return (
    <RefreshingState refreshing={refreshing}>
    <div
      className={containerClassName}
      {...(template.height && { style: { height: `${template.height}px` } })}
    >
      <AutoSizedPlot
        chartId={template.name}
        data={data}
        layout={layout}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ["select2d", "lasso2d", "autoScale2d"],
          modeBarButtonsToAdd: [
            {
              name: "downloadData",
              title: "Download data as a csv",
              icon: ArchiveIcon,
              click: () => downloadData(template, data),
            },
          ],
        }}
      />
    </div>
    </RefreshingState>
  );
};

export default BarChart;
